// ---------------------------------------------------------------------------
// McpGatewayFallback — KgServerFallback backed by cogo-mcp-gateway.
//
// Phase 1b Step 1b.4 per .planning/kg-repository-interface.md v1.0 §7.1.
//
// Wire format: JSON-RPC 2.0 over HTTPS. Envelope verified against
// cogo-platform/apps/cogo-agent/supabase/functions/cogo-mcp-gateway/index.ts:
//
//   Request  = { jsonrpc:"2.0", id, method:"tools/call",
//                params:{ name:"cogo.design.<method>", arguments:{...} } }
//   Response = { jsonrpc:"2.0", id,
//                result:{ content:[ {type:"text", text:<JSON-string>} ] } }
//        OR   = { jsonrpc:"2.0", id, error:{ code, message, data? } }
//
// Caller parses `result.content[0].text` as JSON to get the tool payload.
// Individual tool response shapes are defined by handlers/design.ts on
// the server; this fallback makes defensive assumptions about structure
// and falls back to empty results on mismatch.
// ---------------------------------------------------------------------------

import type {
  ComponentInfo,
  ComponentStats,
  KgServerFallback,
} from "../types"

export interface McpGatewayFallbackConfig {
  /** Full mcp-gateway URL, e.g. "https://<project>.supabase.co/functions/v1/cogo-mcp-gateway". */
  url: string
  /** Optional Supabase JWT (Bearer token) for authenticated requests. */
  getAuthToken?: () => Promise<string | null | undefined>
  /** Optional fetch override for testing / server-side use. */
  fetchImpl?: typeof fetch
  /** Optional abort controller deadline (ms). Default: 10000. */
  timeoutMs?: number
}

interface JsonRpcErrorResponse {
  jsonrpc: "2.0"
  id: number | string
  error: { code: number; message: string; data?: unknown }
}

interface JsonRpcSuccessResponse {
  jsonrpc: "2.0"
  id: number | string
  result: { content: Array<{ type: string; text: string }> }
}

type JsonRpcResponse = JsonRpcSuccessResponse | JsonRpcErrorResponse

function isErrorResponse(r: JsonRpcResponse): r is JsonRpcErrorResponse {
  return "error" in r
}

/** True when v is a non-empty string. */
function str(v: unknown): string | undefined {
  return typeof v === "string" && v.length > 0 ? v : undefined
}

/**
 * Build a ComponentInfo from an unknown tool-response row. Defensive —
 * accepts several shapes (server handler may evolve):
 *   { iri, name, category?, description? }                           // Phase 1a compatible
 *   { namespace, name, title?, description? }                        // DESIGN_TOOLS v1
 *   { registry_namespace, name, title?, description?, component_type? } // mcp-gateway live shape
 *
 * The `title` / `description` / `component_type` fields may be explicit
 * `null` (observed in live gateway) — treat null the same as missing.
 * Phase 2b KgSmoke observed the live gateway shape dropped all rows; see
 * `.planning/phase-2b-judge-first-consumer-results.md` §5.4 #2.
 */
function toComponentInfo(raw: unknown): ComponentInfo | null {
  if (!raw || typeof raw !== "object") return null
  const r = raw as Record<string, unknown>
  const namespaceLike = str(r.namespace) ?? str(r.registry_namespace)
  const slug = str(r.name)
  const iri = str(r.iri) ?? (namespaceLike && slug ? `${namespaceLike}#${slug}` : undefined)
  // Prefer `title` for display; fall back to `name`. `title` may be null
  // for un-annotated registry entries — `str()` normalizes to undefined.
  const display = str(r.title) ?? slug
  if (!iri || !display) return null
  return {
    iri,
    name: display,
    category: str(r.category) ?? str(r.component_type),
    description: str(r.description),
  }
}

/**
 * Accept both flat (`totalComponents: N`) and nested
 * (`components: { total: N }`) shapes. The live gateway `get_stats` returns
 * the nested form keyed by `registries` / `components` / `patterns_7d`; the
 * Phase 1a contract was flat. Both map to ComponentStats.
 */
function nestedTotal(raw: Record<string, unknown>, key: string): number {
  const bucket = raw[key]
  if (bucket && typeof bucket === "object" && "total" in bucket) {
    const t = (bucket as Record<string, unknown>).total
    if (typeof t === "number") return t
    const n = Number(t)
    return Number.isFinite(n) ? n : 0
  }
  return 0
}

function toComponentStats(raw: unknown): ComponentStats | null {
  if (!raw || typeof raw !== "object") return null
  const r = raw as Record<string, unknown>
  const totalComponents =
    Number(r.totalComponents ?? r.total_components ?? nestedTotal(r, "components"))
  const totalEvents =
    Number(r.totalEvents ?? r.total_events ?? nestedTotal(r, "events"))
  const totalActions =
    Number(r.totalActions ?? r.total_actions ?? nestedTotal(r, "actions"))
  return {
    totalComponents: Number.isFinite(totalComponents) ? totalComponents : 0,
    totalEvents: Number.isFinite(totalEvents) ? totalEvents : 0,
    totalActions: Number.isFinite(totalActions) ? totalActions : 0,
    shapeSha: String(r.shapeSha ?? r.shape_sha ?? ""),
    coreVersion: String(r.coreVersion ?? r.core_version ?? ""),
    oxigraphVersion: String(r.oxigraphVersion ?? r.oxigraph_version ?? ""),
  }
}

export class McpGatewayFallback implements KgServerFallback {
  constructor(private readonly config: McpGatewayFallbackConfig) {}

  async searchComponents(query: string): Promise<ComponentInfo[]> {
    const payload = await this.call("cogo.design.search_components", { query })
    if (!payload) return []
    const arr = Array.isArray(payload)
      ? payload
      : Array.isArray((payload as Record<string, unknown>)?.components)
        ? ((payload as Record<string, unknown>).components as unknown[])
        : []
    return arr.map(toComponentInfo).filter((c): c is ComponentInfo => c !== null)
  }

  async getComponent(iri: string): Promise<ComponentInfo | null> {
    // mcp-gateway handler takes { registry, name } — derive from IRI.
    const { registry, name } = this.splitIri(iri)
    const payload = await this.call("cogo.design.get_component", {
      registry,
      name,
    })
    if (!payload) return null
    // Handler may return the component directly or wrapped in { component }.
    const body =
      (payload as Record<string, unknown>)?.component ?? payload
    return toComponentInfo(body)
  }

  async getStats(): Promise<ComponentStats | null> {
    const payload = await this.call("cogo.design.get_stats", {})
    return toComponentStats(payload)
  }

  // ——— Internals ———

  private async call(
    tool: string,
    args: Record<string, unknown>,
  ): Promise<unknown> {
    const fetcher = this.config.fetchImpl ?? fetch
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    }
    if (this.config.getAuthToken) {
      const tok = await this.config.getAuthToken()
      if (tok) headers["Authorization"] = `Bearer ${tok}`
    }
    const body = JSON.stringify({
      jsonrpc: "2.0",
      id: Date.now(),
      method: "tools/call",
      params: { name: tool, arguments: args },
    })
    const controller = new AbortController()
    const timer = setTimeout(
      () => controller.abort(),
      this.config.timeoutMs ?? 10000,
    )
    try {
      const res = await fetcher(this.config.url, {
        method: "POST",
        headers,
        body,
        signal: controller.signal,
      })
      if (!res.ok) {
        throw new Error(
          `[kg-repository mcp-gateway] ${tool} HTTP ${res.status} ${res.statusText}`,
        )
      }
      const envelope = (await res.json()) as JsonRpcResponse
      if (isErrorResponse(envelope)) {
        throw new Error(
          `[kg-repository mcp-gateway] ${tool} JSON-RPC error ${envelope.error.code}: ${envelope.error.message}`,
        )
      }
      const content = envelope.result?.content?.[0]
      if (!content || content.type !== "text") {
        return null
      }
      try {
        return JSON.parse(content.text) as unknown
      } catch {
        // Some tools may return plain text — return as-is.
        return content.text
      }
    } finally {
      clearTimeout(timer)
    }
  }

  /**
   * Heuristic IRI split into (registry, name). Accepts:
   *   "https://.../ns/<registry>#<name>"  → (registry, name)
   *   "<registry>#<name>"                 → (registry, name)
   *   "<name>"                            → ("", name)
   */
  private splitIri(iri: string): { registry: string; name: string } {
    const hashIdx = iri.indexOf("#")
    if (hashIdx >= 0) {
      const before = iri.slice(0, hashIdx)
      const after = iri.slice(hashIdx + 1)
      const lastSlash = before.lastIndexOf("/")
      const registry = lastSlash >= 0 ? before.slice(lastSlash + 1) : before
      return { registry, name: after }
    }
    return { registry: "", name: iri }
  }
}
